/**
 * RTWO System Registry — Domain Models
 * Phase 6.5 | Version: 1.0 Registry Edition
 *
 * Strongly typed domain models for the RTWO System Registry.
 * Acts as the internal service directory for all RTWO engines and modules.
 *
 * Truthful Data Policy: This is a registry and discovery layer only.
 * It must not create live blockchain activity, claim RTWO Mainnet is online,
 * or claim any future module is complete.
 */

// ─────────────────────────────────────────────
// REGISTRY PRIMITIVES
// ─────────────────────────────────────────────

export type ModuleId = string;
export type DependencyId = string;
export type CapabilityId = string;
export type SourceId = string;
export type ReadinessId = string;
export type RegistryTimestamp = number | null;

export const REGISTRY_VERSION = {
  version: '1.0.0-phase6.5',
  phase: 'Phase 6.5 — System Registry & Module Discovery',
  status: 'Registry Active — Modules Pre-Registered',
} as const;

// ─────────────────────────────────────────────
// MODULE TYPE
// ─────────────────────────────────────────────

export type RTWOModuleType =
  | 'core_engine'
  | 'protocol_engine'
  | 'consensus_engine'
  | 'ledger_engine'
  | 'storage_layer'
  | 'network_engine'
  | 'status_manager'
  | 'enterprise_services'
  | 'cryptography_engine'
  | 'wallet_engine'
  | 'validator_infrastructure'
  | 'rpc_platform'
  | 'explorer_platform'
  | 'governance_runtime'
  | 'staking_engine'
  | 'security_operations'
  | 'monitoring_observability'
  | 'developer_platform'
  | 'enterprise_ux'
  | 'production_infrastructure'
  | 'testnet'
  | 'mainnet';

// ─────────────────────────────────────────────
// MODULE PHASE
// ─────────────────────────────────────────────

export type RTWOModulePhase =
  | 'phase_1'
  | 'phase_2'
  | 'phase_3'
  | 'phase_4'
  | 'phase_5'
  | 'phase_6'
  | 'phase_6_5'
  | 'phase_7'
  | 'phase_8'
  | 'phase_9'
  | 'phase_10'
  | 'phase_11'
  | 'phase_12'
  | 'phase_12_5'
  | 'phase_13'
  | 'phase_14'
  | 'phase_14_5'
  | 'phase_15'
  | 'phase_16'
  | 'phase_17'
  | 'phase_18'
  | 'future';

// ─────────────────────────────────────────────
// MODULE STATUS (Truthful States Only)
// ─────────────────────────────────────────────

export type RTWOModuleStatus =
  | 'registered'
  | 'structure_complete'
  | 'waiting_for_real_backend'
  | 'not_connected'
  | 'not_configured'
  | 'future_phase'
  | 'production_pending'
  | 'operational'
  | 'error';

export const MODULE_STATUS_LABELS: Record<RTWOModuleStatus, string> = {
  registered: 'Registered',
  structure_complete: 'Structure Complete',
  waiting_for_real_backend: 'Waiting for Real Backend',
  not_connected: 'Not Connected',
  not_configured: 'Not Configured',
  future_phase: 'Future Phase',
  production_pending: 'Production Pending',
  operational: 'Operational',
  error: 'Error',
};

export const MODULE_STATUS_COLORS: Record<RTWOModuleStatus, string> = {
  registered: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  structure_complete: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  waiting_for_real_backend: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  not_connected: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  not_configured: 'text-red-400 bg-red-400/10 border-red-400/20',
  future_phase: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
  production_pending: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  operational: 'text-green-400 bg-green-400/10 border-green-400/20',
  error: 'text-red-500 bg-red-500/10 border-red-500/20',
};

// ─────────────────────────────────────────────
// DEPENDENCY TYPE
// ─────────────────────────────────────────────

export type RTWODependencyType =
  'required' | 'optional' | 'peer' | 'runtime' | 'build_time' | 'future';

// ─────────────────────────────────────────────
// DEPENDENCY STATUS
// ─────────────────────────────────────────────

export type RTWODependencyStatus =
  'satisfied' | 'missing' | 'future_phase' | 'not_configured' | 'error';

// ─────────────────────────────────────────────
// CAPABILITY TYPE
// ─────────────────────────────────────────────

export type RTWOCapabilityType =
  | 'data_model'
  | 'service_interface'
  | 'orchestration'
  | 'event_publishing'
  | 'lifecycle_management'
  | 'storage_abstraction'
  | 'network_protocol'
  | 'consensus_protocol'
  | 'cryptography'
  | 'rpc'
  | 'explorer'
  | 'governance'
  | 'monitoring'
  | 'security'
  | 'enterprise';

// ─────────────────────────────────────────────
// CAPABILITY STATUS
// ─────────────────────────────────────────────

export type RTWOCapabilityStatus =
  'defined' | 'implemented' | 'placeholder' | 'future_phase' | 'not_configured';

// ─────────────────────────────────────────────
// HEALTH SOURCE TYPE
// ─────────────────────────────────────────────

export type RTWOHealthSourceType =
  | 'internal_orchestrator'
  | 'external_api'
  | 'database'
  | 'network_probe'
  | 'rpc_endpoint'
  | 'not_configured';

// ─────────────────────────────────────────────
// HEALTH SOURCE STATUS
// ─────────────────────────────────────────────

export type RTWOHealthSourceStatus =
  'active' | 'not_configured' | 'not_connected' | 'error' | 'future_phase';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOModuleRegistration
// ─────────────────────────────────────────────

export interface RTWOModuleRegistration {
  moduleId: ModuleId;
  moduleName: string;
  moduleType: RTWOModuleType;
  version: string;
  phase: RTWOModulePhase;
  status: RTWOModuleStatus;
  description: string;
  registeredAt: RegistryTimestamp;
  updatedAt: RegistryTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOModuleDependency
// ─────────────────────────────────────────────

export interface RTWOModuleDependency {
  dependencyId: DependencyId;
  moduleId: ModuleId;
  dependsOnModuleId: ModuleId;
  dependencyType: RTWODependencyType;
  required: boolean;
  status: RTWODependencyStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOModuleCapability
// ─────────────────────────────────────────────

export interface RTWOModuleCapability {
  capabilityId: CapabilityId;
  moduleId: ModuleId;
  capabilityName: string;
  capabilityType: RTWOCapabilityType;
  status: RTWOCapabilityStatus;
  description: string;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOModuleHealthSource
// ─────────────────────────────────────────────

export interface RTWOModuleHealthSource {
  sourceId: SourceId;
  moduleId: ModuleId;
  sourceType: RTWOHealthSourceType;
  sourceName: string;
  status: RTWOHealthSourceStatus;
  lastCheckedAt: RegistryTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOModuleReadiness
// ─────────────────────────────────────────────

export interface RTWOModuleReadiness {
  readinessId: ReadinessId;
  moduleId: ModuleId;
  architectureReady: boolean;
  interfacesReady: boolean;
  dashboardReady: boolean;
  realBackendConnected: boolean;
  productionReady: boolean;
  notes: string;
}

// ─────────────────────────────────────────────
// REGISTRY EVENT
// ─────────────────────────────────────────────

export type RegistryEventType =
  | 'module_registered'
  | 'module_updated'
  | 'dependency_registered'
  | 'capability_discovered'
  | 'health_source_updated'
  | 'readiness_updated';

export interface RegistryEvent {
  eventId: string;
  eventType: RegistryEventType;
  moduleId: ModuleId;
  timestamp: RegistryTimestamp;
  payload: Record<string, unknown>;
}

// ─────────────────────────────────────────────
// SYSTEM MAP
// ─────────────────────────────────────────────

export interface RTWOSystemMap {
  generatedAt: RegistryTimestamp;
  totalModules: number;
  registeredModules: number;
  futureModules: number;
  totalDependencies: number;
  totalCapabilities: number;
  productionReadyCount: number;
  modules: RTWOModuleRegistration[];
  dependencyEdges: RTWOModuleDependency[];
}
